import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText, interpolate } from "shared/lib";

import { useCouncilLocalisation } from "shared/hooks";

import { ExternalLinkIcon } from "shared/components";

export interface SSCProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

export function SSC({ assessmentLights, conversation }: SSCProps) {
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const renderPerformTitleSearch = () => {
    return (
      <div>
        <h3 className="text-headingmd">{getText("Perform a title search")}</h3>
        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't check for restrictions like easements or covenants that can affect your property. You can get a current title search online through Titles Queensland to see this information.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href={getLocalisationEntry("councilApplyForCertificateOfTitleLink")}
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Titles Queensland")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderConfirmIfBuildingApprovalIsNeeded = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Confirm if you need building approval")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Development approval (under the planning scheme) is separate from building approval. Building approvals ensure your construction work is safe and meets the Building Act 1975 and national codes.",
          )}
        </p>
        <p className="text-bodymd mb-2">
          {getText(
            "You must engage a private building certifier to determine if you need a building approval.",
          )}
        </p>
      </div>
    );
  };

  const renderGetWrittenConfirmationFromBuilder = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Get written confirmation for your builder")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            'Your builder may need proof that your project is "Accepted Development" under the planning scheme before they start. You can request this advice from Council\'s Development Information Officers.',
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.sunshinecoast.qld.gov.au/development/development-applications/development-advice-services/request-for-written-advice"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request written advice")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderSeekPreLodgementAdvice = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Seek pre-lodgement advice")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Your proposal requires a Development Application (Impact Assessment). This is the highest level of assessment. We strongly recommend seeking pre-lodgement advice from a Council Planner to discuss application requirements, potential issues, and the assessment process before you invest in preparing your application.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.sunshinecoast.qld.gov.au/development/development-applications/development-advice-services/pre-lodgement-meeting"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Book a pre-lodgement meeting")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderPrepareDevelopmentApplication = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Prepare your Development Application")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Getting your application right the first time is the best way to avoid delays. Impact Assessable applications require detailed supporting information. Use the checklists on Council’s website to ensure your submission is complete.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.sunshinecoast.qld.gov.au/development/development-applications/development-application-forms"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Application checklists")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderLodgeYourApplication = () => {
    return (
      <div>
        <h3 className="text-headingmd">{getText("Lodge your application")}</h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Once all your plans and supporting reports are ready, you can formally lodge your Development Application with Council through the online portal.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.sunshinecoast.qld.gov.au/development/development-applications/lodging-a-development-application"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Lodge your Development Application")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderUnderstandWhatThisMeans = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Understand what this means")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Our tool has determined that your proposal is classified as Prohibited Development under the Sunshine Coast Planning Scheme 2014. This is the most restrictive category and it means that a development application cannot be made or approved for this proposal.",
          )}
        </p>
      </div>
    );
  };

  const renderDiscussWithUs1 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your proposal with us")}
        </h3>
        <p className="text-bodymd mb-2">
          {interpolate(
            "Have questions? You can book a meeting to speak with a Council Development Information Officer. When you {{link}} tell us your report ID:",
            {
              link: (
                <a
                  className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                  href={getLocalisationEntry("councilContactLink")}
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("contact us")}
                </a>
              ),
            },
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  const renderDiscussWithUs2 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your proposal with us")}
        </h3>
        <p className="text-bodymd mb-2">
          {interpolate(
            "The best way to understand your options is to discuss your myLot report with a Development Information Officer. Your myLot report pinpoints exactly which planning scheme code(s) your proposal needs to address. Have your report ID ready when you {{link}}, as it will make the conversation faster and more effective.",
            {
              link: (
                <a
                  className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                  href={getLocalisationEntry("councilContactLink")}
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("contact us")}
                </a>
              ),
            },
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  const renderDiscussWithUs4 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your options with Council")}
        </h3>
        <p className="text-bodymd mb-2">
          {interpolate(
            "A Council Development Information Officer can further explain the constraints on your property and discuss if any entirely different development concepts might be possible. Please have your myLot report ID ready when you {{link}}, as it will make the conversation faster and more effective.",
            {
              link: (
                <a
                  className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                  href={getLocalisationEntry("councilContactLink")}
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("contact us")}
                </a>
              ),
            },
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderPerformTitleSearch()}
          {renderConfirmIfBuildingApprovalIsNeeded()}
          {renderGetWrittenConfirmationFromBuilder()}
          {renderDiscussWithUs1()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderPerformTitleSearch()}
          {renderDiscussWithUs2()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderPerformTitleSearch()}
          {renderSeekPreLodgementAdvice()}
          {renderPrepareDevelopmentApplication()}
          {renderLodgeYourApplication()}
        </>
      );
    case AssessmentLights.Prohibited:
      return (
        <>
          {renderUnderstandWhatThisMeans()}
          {renderDiscussWithUs4()}
        </>
      );
  }
}
